import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { FC, MutableRefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { SmsOptInCheckoutStyleFinePrint } from 'src/components/SmsOptInCheckoutStyleFinePrint';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { shouldShowSmsOptInComplianceUi } from 'src/models/platform/smsUtils';
import {
  NotificationPreferencesUserByHash,
  NotificationSetting,
  SessionUser,
} from 'src/models/user/_types';
import { updateSmsOptIn } from 'src/models/user/user';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import { useNotification } from './useNotification';

export interface NotificationsLayoutProps {
  hashid?: string | null;
  recipientUser?: NotificationPreferencesUserByHash;
  /** When false / omitted, the "Customize emails" link is hidden (standalone or no permission). */
  showCustomizeEmailsLink?: boolean;
  standalone?: boolean;
}

type DisplayUser = NotificationPreferencesUserByHash | SessionUser;

export const NotificationsLayout: FC<NotificationsLayoutProps> = ({
  hashid,
  recipientUser,
  showCustomizeEmailsLink = false,
  standalone = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'notifications',
  });

  const queryClient = useQueryClient();

  const userFormikRef = useRef<any>(null);
  const platformFormikRef = useRef<any>(null);

  const { session, user, refreshUser } = useOptionalSession();
  const displayUser: DisplayUser | null | undefined = recipientUser ?? user;

  const apiHash =
    standalone && hashid && !user?.id ? hashid : undefined;

  const { notifications, isLoading, updateNotificationSettingsMutation } =
    useNotification({
      hash: apiHash,
      hidePromotionalEmail:
        apiHash != null && apiHash !== ''
          ? recipientUser?.isHeadTrainer ?? false
          : undefined,
    });

  const [smsOptInLoading, setSmsOptInLoading] = useState(false);

  const smsOptIn = displayUser?.smsOptedOut === false;

  const handleSmsOptInChange = async (checked: boolean) => {
    if (!displayUser?.id) {
      return;
    }
    setSmsOptInLoading(true);
    try {
      await updateSmsOptIn({
        smsOptedOut: !checked,
        hash: apiHash ?? undefined,
      });
      toast.success(t('smsPreferenceSaved'));
      if (apiHash) {
        await queryClient.invalidateQueries({
          queryKey: ['userByHash', apiHash],
        });
      } else {
        await refreshUser();
      }
    } catch (error) {
      toast.error(api.formatError(error, t('smsPreferenceError')));
    } finally {
      setSmsOptInLoading(false);
    }
  };

  const columns = [
    {
      id: 'title',
      label: t('emailType'),
      format: (value: string, row: NotificationSetting) => {
        return (
          <Stack>
            <Typography variant="subtitle1">{value}</Typography>
            <HtmlTypography
              variant="body2"
              component={'span'}
              sx={{
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {row.platform
                ? row.platformDescription || row.description
                : !row.platform &&
                  (displayUser?.isTrainer
                    ? row.trainerDescription || row.description
                    : row.description)}
            </HtmlTypography>
          </Stack>
        );
      },
    },
  ];

  const RowAction = ({
    row,
    name,
    formikRef,
  }: {
    formikRef: MutableRefObject<any>;
    name: string;
    row: NotificationSetting;
  }) => {
    return (
      <Tooltip
        title={
          row.disabled
            ? t('thisSettingIsDisabledByYourPlatformAdministrator')
            : ''
        }
      >
        <Stack sx={{ width: '100%', alignItems: 'flex-end' }}>
          <Checkbox
            checked={row.active === 'true' || row.active === true}
            disabled={row.disabled}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              formikRef.current.setFieldValue(name, e.target.checked);
            }}
          />
        </Stack>
      </Tooltip>
    );
  };

  const submitUserHandler = async (values, isUnsubscribe) => {
    const userNotifications = notifications.filter((item) => !item.platform);
    const platformNotifications = notifications.filter((item) => item.platform);

    const data = [
      ...userNotifications.map((item) => {
        if (isUnsubscribe) {
          return { ...item, active: false };
        }
        const obj = values.find(
          (i) => i.id === item.id && i.platform === item.platform
        );
        return { ...item, active: obj?.active };
      }),
      ...platformNotifications, // Keep platform notifications unchanged
    ];

    try {
      await updateNotificationSettingsMutation.mutateAsync(data);
      toast.success(isUnsubscribe ? t('unsubscribeSuccess') : t('saveSuccess'));
      isUnsubscribe && userFormikRef.current.resetForm();
    } catch (error) {
      toast.error(
        api.formatError(
          error,
          isUnsubscribe ? t('unsubscribeError') : t('saveError')
        )
      );
    }
  };

  const submitPlatformHandler = async (values, isUnsubscribe) => {
    const userNotifications = notifications.filter((item) => !item.platform);
    const platformNotifications = notifications.filter((item) => item.platform);

    const data = [
      ...userNotifications, // Keep user notifications unchanged
      ...platformNotifications.map((item) => {
        if (isUnsubscribe) {
          return { ...item, active: false };
        }
        const obj = values.find(
          (i) => i.id === item.id && i.platform === item.platform
        );
        return { ...item, active: obj?.active };
      }),
    ];

    try {
      await updateNotificationSettingsMutation.mutateAsync(data);
      toast.success(isUnsubscribe ? t('unsubscribeSuccess') : t('saveSuccess'));
      isUnsubscribe && platformFormikRef.current.resetForm();
    } catch (error) {
      toast.error(
        api.formatError(
          error,
          isUnsubscribe ? t('unsubscribeError') : t('saveError')
        )
      );
    }
  };

  const userNotifications = notifications.filter((item) => !item.platform);
  const platformNotifications = notifications.filter((item) => item.platform);

  const userFormikProps = {
    initialValues: userNotifications,
    enableReinitialize: true,
    onSubmit: () => void 0,
  };

  const platformFormikProps = {
    initialValues: platformNotifications,
    enableReinitialize: true,
    onSubmit: () => void 0,
  };

  const showCustomizeEmails = !standalone && showCustomizeEmailsLink;

  return (
    <>
      <Meta title={t('title')} brand={session?.brand} />
      {!standalone ? (
        <AppFrameConfig
          id="notifications"
          containerMaxWidth={'lg'}
          title={t('title')}
          actions={
            showCustomizeEmails ? (
              <Link component={RouterLink} to={'/emails'}>
                <Icon icon={faExternalLink} size="small" color="primary" />{' '}
                {t('customizeEmails')}
              </Link>
            ) : undefined
          }
        />
      ) : null}
      <Stack spacing={4} mb={4}>
        {session && shouldShowSmsOptInComplianceUi(session) && (
          <StickyCard
            title={t('smsNotifications')}
            subtitle={t('smsNotificationsSubtitle')}
          >
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Checkbox
                  checked={smsOptIn}
                  disabled={smsOptInLoading}
                  onChange={(e) => handleSmsOptInChange(e.target.checked)}
                />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1">
                    {t('smsOptInCheckboxLabel')}
                  </Typography>
                  <SmsOptInCheckoutStyleFinePrint
                    brand={session?.brand}
                    disclosureKeyPrefix="notifications"
                  />
                </Stack>
              </Stack>
            </Box>
          </StickyCard>
        )}

        <Formik {...userFormikProps} innerRef={userFormikRef}>
          {({ values }) => {
            const userActions = (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="prominent"
                  color="primary"
                  loading={updateNotificationSettingsMutation.isPending}
                  disabled={updateNotificationSettingsMutation.isPending}
                  onClick={() => submitUserHandler(values, false)}
                >
                  {t('saveChanges')}
                </Button>
                <Button
                  variant="bordered"
                  loading={updateNotificationSettingsMutation.isPending}
                  disabled={updateNotificationSettingsMutation.isPending}
                  onClick={() => submitUserHandler(values, true)}
                >
                  {t('unsubscribeFromAll')}
                </Button>
              </Stack>
            );

            return (
              <StickyCard
                title={t('myNotificationSettings')}
                subtitle={t('customizeWhichEmailsAndNotificationsYouReceive')}
                actions={userActions}
              >
                <DataTable
                  columns={columns}
                  rows={values}
                  loading={isLoading}
                  fixedActions
                  actionTitle={t('active')}
                  rowActions={(row, rowIndex) => (
                    <RowAction
                      row={row as NotificationSetting}
                      name={`${rowIndex}.active`}
                      formikRef={userFormikRef}
                    />
                  )}
                />
              </StickyCard>
            );
          }}
        </Formik>

        {platformNotifications.length > 0 && (
          <Formik {...platformFormikProps} innerRef={platformFormikRef}>
            {({ values }) => {
              const platformActions = (
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="prominent"
                    color="primary"
                    loading={updateNotificationSettingsMutation.isPending}
                    disabled={updateNotificationSettingsMutation.isPending}
                    onClick={() => submitPlatformHandler(values, false)}
                  >
                    {t('saveChanges')}
                  </Button>
                  <Button
                    variant="bordered"
                    loading={updateNotificationSettingsMutation.isPending}
                    disabled={updateNotificationSettingsMutation.isPending}
                    onClick={() => submitPlatformHandler(values, true)}
                  >
                    {t('disableAll')}
                  </Button>
                </Stack>
              );

              return (
                <StickyCard
                  title={t('platformWideNotificationSettings')}
                  subtitle={t(
                    'customizeWhichEmailsAndNotificationsYourUsersReceive'
                  )}
                  actions={platformActions}
                >
                  <DataTable
                    columns={columns}
                    rows={values}
                    loading={isLoading}
                    fixedActions
                    actionTitle={t('active')}
                    rowActions={(row, rowIndex) => (
                      <RowAction
                        row={row as NotificationSetting}
                        name={`${rowIndex}.active`}
                        formikRef={platformFormikRef}
                      />
                    )}
                  />
                </StickyCard>
              );
            }}
          </Formik>
        )}
      </Stack>
    </>
  );
};
